import CostAwarePrice from "components/shared/costAwarePrice";
import { dateToView } from "helpers/date";
import { featureModuleTypesEnum, paymentPeriodTypes } from "helpers/enums";
import { HiddenDataWrapper, hiddenUserData } from "helpers/hiddenData";
import { enumLabel, formatNumber, ymEvent } from "helpers/system";
import Tooltip from "rc-tooltip";
import React, { useCallback } from "react";
import PaymentStatus from "./paymentStatus";

const Pricing = (props) => {
    const { bid, enums, roles, organization, bidPolicy, modal, profile, refresh } = props;
    const {
        id,
        price,
        priceWithoutCosts,
        costs,
        ndsTypeId,
        paymentTypeId,
        paymentPeriodType,
        paymentPeriodInDays,
        routeCost,
        marginality,
        marginalityPerDay,
        estimatedMarginalityPerDay,
        payment,
    } = bid;

    const nds = enums.nDSType.find((item) => item.value === ndsTypeId);
    const paymentType = enums.paymentType.find((item) => item.value === paymentTypeId);
    const canSeePaymentStatus =
        !roles.isTruckingOrganizationDispatcher &&
        (profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable) || !!payment);
    const canCreateDocuments =
        profile.availableFeatureModules.includes(featureModuleTypesEnum.CrStart) &&
        !roles.isTruckingOrganizationExtendedViewer;

    const openDocumentsModal = useCallback(() => {
        ymEvent("CreationOfDocuments");
        modal({
            title: "Сформировать документы",
            name: "bidDocuments",
            windowMod: "wide",
            bid,
            profile,
            organization,
            roles,
            bidType: "bid",
        });
    }, [id]);

    return (
        <>
            {price >= 0 && (
            <>
                <div className="show-bid__main-info--title">Стоимость</div>
                <div className="show-bid__main-info--value" data-test-id="price">
                    {hiddenUserData(
                        formatNumber(price, { style: "currency", currency: organization?.currency }),
                        { type: "price" },
                        roles.isTruckingOrganizationDispatcher,
                    )}
                    &nbsp;
                    <HiddenDataWrapper hidden={roles.isTruckingOrganizationDispatcher}>
                        {paymentType && nds && (
                            <span className="text-muted">({`${paymentType.label}, ${nds.label}`})</span>
                        )}
                    </HiddenDataWrapper>
                    {canCreateDocuments && (
                        <Tooltip
                            overlay="Формирование документов (счет, акт, счет-фактура, договор заявка,...)"
                            placement="bottom"
                            overlayClassName="mw-200"
                        >
                            <div
                                className="show-bid__link d-inline-block"
                                data-onboarding="bid-documents-link"
                                onClick={openDocumentsModal}
                            >
                                Сформировать документы
                            </div>
                        </Tooltip>
                    )}
                </div>
                {canSeePaymentStatus && (
                    <>
                        <div className="show-bid__main-info--title">Статус оплаты</div>
                        <div className="show-bid__main-info--value" data-test-id="payment-status">
                            <PaymentStatus
                                bid={bid}
                                bidId={id}
                                bidPolicy={bidPolicy}
                                dataTestId="payment-status-control"
                                profile={profile}
                                refresh={refresh}
                                roles={roles}
                            />
                        </div>
                    </>
                )}
                {bidPolicy.canSeeMarginality() && (
                    <>
                        {!!routeCost && (
                            <>
                                <div className="show-bid__main-info--title">Себестоимость маршрута</div>
                                <div className="show-bid__main-info--value" data-test-id="routeCost">
                                    {hiddenUserData(
                                        formatNumber(routeCost, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        }),
                                        { type: "price" },
                                        roles.isTruckingOrganizationDispatcher,
                                    )}
                                </div>
                            </>
                        )}
                        {!!marginality && (
                            <>
                                <div className="show-bid__main-info--title">Маржинальность</div>
                                <div className="show-bid__main-info--value" data-test-id="marginality">
                                    {hiddenUserData(
                                        formatNumber(marginality, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        }),
                                        { type: "price" },
                                        roles.isTruckingOrganizationDispatcher,
                                    )}
                                </div>
                            </>
                        )}
                        {!!marginalityPerDay && (
                            <>
                                <div className="show-bid__main-info--title">Маржинальность на день</div>
                                <div className="show-bid__main-info--value" data-test-id="marginalityPerDay">
                                    {hiddenUserData(
                                        formatNumber(marginalityPerDay, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        }),
                                        { type: "price" },
                                        roles.isTruckingOrganizationDispatcher,
                                    )}
                                </div>
                            </>
                        )}
                        {!!estimatedMarginalityPerDay && (
                            <>
                                <div className="show-bid__main-info--title">Прогноз маржинальности на день</div>
                                <div className="show-bid__main-info--value" data-test-id="estimatedMarginalityPerDay">
                                    {hiddenUserData(
                                        formatNumber(estimatedMarginalityPerDay, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        }),
                                        { type: "price" },
                                        roles.isTruckingOrganizationDispatcher,
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
                {(paymentPeriodType || paymentPeriodInDays !== null) && (
                    <>
                        <div className="show-bid__main-info--title">Срок оплаты</div>
                        <div className="show-bid__main-info--value" data-test-id="paymentPeriod">
                            {hiddenUserData(
                                formatNumber(paymentPeriodInDays),
                                {},
                                roles.isTruckingOrganizationDispatcher,
                            )}
                            &nbsp;
                            <HiddenDataWrapper hidden={roles.isTruckingOrganizationDispatcher}>
                                {paymentPeriodType && (
                                    <span className="text-muted">
                                        ({enumLabel(paymentPeriodTypes, paymentPeriodType)})
                                    </span>
                                )}
                            </HiddenDataWrapper>
                        </div>
                    </>
                )}
                {payment && (
                    <>
                        <div className="show-bid__main-info--title">Дата выставления счета</div>
                        <div className="show-bid__main-info--value" data-test-id="invoice-date">
                            {hiddenUserData(dateToView(payment.invoiceDate))}
                        </div>
                        <div className="show-bid__main-info--title">Плановая дата платежа</div>
                        <div className="show-bid__main-info--value" data-test-id="plan-payment-date">
                            {hiddenUserData(dateToView(payment.planPaymentDate))}
                        </div>
                        {payment.factPaymentDate && (
                            <>
                                <div className="show-bid__main-info--title">Фактическая дата платежа</div>
                                <div className="show-bid__main-info--value" data-test-id="fact-payment-date">
                                    {hiddenUserData(dateToView(payment.factPaymentDate))}
                                </div>
                            </>
                        )}
                        {payment.remainingPayment && (
                            <>
                                <div className="show-bid__main-info--title">Остаток к оплате</div>
                                <div className="show-bid__main-info--value" data-test-id="plan-remaining-payment">
                                    {hiddenUserData(
                                        formatNumber(payment.remainingPayment, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        }),
                                        { type: "price" },
                                        roles.isTruckingOrganizationDispatcher,
                                    )}
                                </div>
                            </>
                        )}
                        {payment.comment && (
                            <>
                                <div className="show-bid__main-info--title">Комментарий</div>
                                <div className="show-bid__main-info--value" data-test-id="plan-payment-comment">
                                    {hiddenUserData(payment.comment)}
                                </div>
                            </>
                        )}
                    </>
                )}
            </>
            )}
            <CostAwarePrice
                costs={costs}
                currency={organization?.currency}
                hidden={roles.isTruckingOrganizationDispatcher}
                modal={modal}
                price={price}
                priceLabel="Цена заявки"
                priceWithoutCosts={priceWithoutCosts}
            />
        </>
    );
};

export default Pricing;
